/** EMP-USER-LINK-001 — Link / Unlink user account modal (moved verbatim from EmployeeProfile.jsx; logic unchanged). */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function LinkAccountModal({ mode, employee, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const isRelink = mode === 'link' && !!employee.linked_user_id;
  const needsReason = mode === 'unlink' || isRelink;

  useEffect(() => {
    if (mode !== 'link') return;
    axios.get(`${API}/api/hr/employees/${employee.employee_id}/link-candidates`, { headers: authHeaders() })
      .then(res => setCandidates(res.data.users || []))
      .catch(() => toast.error('Failed to load user accounts'));
  }, [mode, employee.employee_id]);

  const canSubmit = !saving
    && (mode === 'unlink' || userId)
    && (!needsReason || reason.trim().length >= 5);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSaving(true);
      const headers = authHeaders();
      if (mode === 'unlink') {
        const res = await axios.post(`${API}/api/hr/employees/${employee.employee_id}/unlink-user`,
          { reason: reason.trim() }, { headers });
        toast.success(res.data.message || 'Account unlinked');
      } else {
        const res = await axios.post(`${API}/api/hr/employees/${employee.employee_id}/link-user`,
          { user_id: userId, reason: reason.trim() || undefined }, { headers });
        toast.success(res.data.message || 'Account linked');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="link-account-modal">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">
          {mode === 'unlink' ? 'Unlink User Account' : isRelink ? 'Change Linked Account' : 'Link User Account'}
        </h2>
        {mode === 'unlink' ? (
          <p className="text-sm text-slate-600">
            Unlink <strong>{employee.linked_user_email}</strong> from {employee.full_name}?
            Historical leave, attendance and payroll records are preserved.
          </p>
        ) : (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">User account</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              data-testid="link-user-select"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="">Select user account…</option>
              {candidates.map(u => (
                <option
                  key={u.id}
                  value={u.id}
                  disabled={!!u.linked_to_employee_id && u.linked_to_employee_id !== employee.employee_id}
                >
                  {u.full_name || u.email} ({u.email})
                  {u.linked_to_employee_id ? ` — linked to ${u.linked_to_employee_id}` : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Accounts already linked to another employee are disabled (1:1 enforced).</p>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Reason {needsReason ? '(mandatory, min 5 characters)' : '(optional)'}
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required={needsReason}
            minLength={needsReason ? 5 : undefined}
            maxLength={200}
            placeholder={mode === 'unlink' ? 'e.g. Employee record created in error' : 'e.g. Correcting initial link'}
            data-testid="link-reason-input"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="link-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={!canSubmit}
            className={mode === 'unlink' ? 'bg-red-600 hover:bg-red-700' : ''}
            data-testid="link-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {mode === 'unlink' ? 'Unlink' : 'Save Link'}
          </Button>
        </div>
      </form>
    </div>
  );
}
